import { Box } from '@material-ui/core';
import {
  A2AProviderName,
  CardPaymentProviderName,
  CreditCardProviderName,
  LenderName,
  PaymentIntent,
} from '@nemuru-eng/js-api-client';

import { SinglePaymentProviderLogo } from '@/components/modules/Application/components/PaymentMethod/_shared/components/SinglePaymentProviderLogo';
import { useWidth } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/hooks/useWidth';
import { PaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/PaymentMethodDetail/PaymentMethodDetailEntity';
import { CopyToClipboardButton } from '@/components/shared/components/CopyToClipboardButton';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { FlexRow } from '@/components/shared/components/FlexRow';
import { Font } from '@/components/shared/components/Font';
import { LenderIcon } from '@/components/shared/components/Logo/LenderLogo/LenderIcon';
import { StatusTag } from '@/components/shared/components/StatusTag/StatusTag';
import { Text } from '@/components/shared/components/Text';
import { STYLE_PARAMS_BY_STATUS, StyleParam } from '@/constants/styleParamsByStatus';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { ProviderName } from '@/types/ProviderName';

interface PaymentIntentBadgeProps {
  paymentIntent: PaymentIntent | null;
}

export const PaymentIntentBadge = ({ paymentIntent }: PaymentIntentBadgeProps) => {
  const { width, ref } = useWidth<HTMLDivElement>();
  const { palette } = useCustomTheme();

  // const applicationType = ApplicationTypeMap.fromPaymentMethodType(paymentIntent.paymentMethodType);
  // const menuItem = ApplicationTypeMap.toMenuItem(applicationType);

  const isMedium = width && width < 160;
  const isSmall = width && width < 140;
  const isVerySmall = width && width < 120;

  if (!paymentIntent) {
    return (
      <CustomPaper style={{ padding: isSmall ? '6px 8px' : '6px 12px', height: 42 }}>
        <div style={{ display: 'flex', alignItems: 'center', gridGap: isSmall ? 4 : 8, height: '100%' }} ref={ref}>
          <Box
            flexShrink={0}
            height={isSmall ? 20 : 24}
            width={isSmall ? 20 : 24}
            bgcolor={palette.grey[100]}
            borderRadius={100}
          />
          <Box minWidth={0} flexGrow={1}>
            <Box minWidth={0} pr={1} display="flex" flexDirection="column">
              <Font variant="caption" color="textSecondary" className="overflow-text-ellipsis">
                <Text tId="NOT_STARTED" />
              </Font>
              <Font variant="caption" color={'textSecondary'} className="overflow-text-ellipsis">
                <Text tId="PENDING_TO_SELECT_PAYMENT_METHOD" />
              </Font>
            </Box>
          </Box>
          <Box></Box>
        </div>
      </CustomPaper>
    );
  }

  const statusTagProps = STYLE_PARAMS_BY_STATUS[paymentIntent.status] as StyleParam[keyof StyleParam] | undefined;
  const provider = paymentIntent.providerName;

  return (
    <CustomPaper fill="paper" style={{ padding: isSmall ? '6px 8px' : '6px 12px', height: 42 }}>
      <div style={{ display: 'flex', alignItems: 'center', gridGap: isSmall ? 4 : 8, height: '100%' }} ref={ref}>
        <Box display="flex" alignItems="center" gridGap={isSmall ? 4 : 8} height="100%" flexGrow={1} minWidth={0}>
          {provider ? (
            <Box
              flexShrink={0}
              height={isSmall ? 20 : 24}
              width={isSmall ? 20 : 24}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <ProviderLogo provider={provider} size={isSmall ? 16 : 20} />
            </Box>
          ) : (
            <Box
              flexShrink={0}
              height={isSmall ? 20 : 24}
              width={isSmall ? 20 : 24}
              bgcolor={palette.grey[100]}
              borderRadius={100}
            />
          )}
          {!isVerySmall && (
            <Box minWidth={0} flexGrow={1}>
              <Box minWidth={0} pr={1} display="flex" flexDirection="column">
                <Font variant="caption" color="textPrimary" className="overflow-text-ellipsis">
                  <Text tId={`${paymentIntent.paymentMethodType}_SHORT`} />
                </Font>
                <FlexRow display="flex" alignItems="center" justifyContent="flex-start" gridGap={2} height={22}>
                  <Font
                    variant="caption"
                    color={paymentIntent.providerRequestId ? 'textPrimary' : 'textSecondary'}
                    className="overflow-text-ellipsis"
                  >
                    {provider ? (
                      paymentIntent.providerRequestId ?? <Text tId="NO_RESPONSE" />
                    ) : PaymentMethodDetailEntity.PAYMENT_INTENT_STATUS_FINAL.includes(paymentIntent.status) ? (
                      <Text tId="PROCESS_TERMINATED" />
                    ) : (
                      <Text tId="PENDING_TO_COMPLETE_PAYMENT" />
                    )}
                  </Font>
                  {paymentIntent.providerRequestId && !isMedium && (
                    <CopyToClipboardButton
                      value={paymentIntent.providerRequestId ?? ''}
                      style={{ color: palette.text.secondary, fontSize: 12 }}
                    />
                  )}
                </FlexRow>
              </Box>
            </Box>
          )}
        </Box>
        <Box>
          {statusTagProps && (
            <StatusTag
              variant={isVerySmall ? 'contained' : isSmall ? 'badge' : 'contained'}
              size="small"
              type={statusTagProps.colorPaletteName}
              colorType={statusTagProps.colorType}
              labelTextId={statusTagProps.title}
            />
          )}
        </Box>
      </div>
    </CustomPaper>
  );
};

interface ProviderLogoProps {
  provider: ProviderName;
  size?: number;
}

const ProviderLogo = ({ provider, size = 16 }: ProviderLogoProps) => {
  if (
    Object.values(LenderName).includes(provider as LenderName) ||
    Object.values(CreditCardProviderName).includes(provider as CreditCardProviderName)
  ) {
    return <LenderIcon size={size} lenderName={provider as LenderName} />;
  }

  return <SinglePaymentProviderLogo size={size} providers={[provider as CardPaymentProviderName | A2AProviderName]} />;
};
